import React, { useState, useEffect } from 'react';
import Btn from '../../../Components/Btn';
import Modal from '../../../Components/Modal';
import ModalBody from '../../../Components/ModalBody';
import { Panel, PanelBody, PanelHeader, PanelFooter } from '../../../Components/Panel/Panel';
import { InstagramEditorServiceType } from '../Services/InstagramEditorService';
import { InstagramMediaContentData } from '../../../Services/InstagramServices';
import ImpersonatingServices from '../../../Services/ImpersonatingServices';
import styles from './InstagramMediaSelector.module.css';
import { Spinner } from '../../../Components/Spinner/Spinner';
import { Checkbox } from '../../../Components/Checkbox/Checkbox2';
import { EmptyState } from '../../../Components/EmptyState/EmptyState';
import { InstagramMediaValue } from '../Utils/LiquidModelProcessing';

type InstagramMediaSelectorType = {
	data: any,
	onChange: any,
	property: any,
	propertyContainer: any,
	instagramServices: () => InstagramEditorServiceType,
	rootScope: any,
	routeParams: any
}

export default function InstagramMediaSelector({
	data,
	onChange,
	property,
	// propertyContainer,
	instagramServices,
	rootScope,
	routeParams
}: InstagramMediaSelectorType) {

	const insta = instagramServices();

	const getFilteredMediaTypes = () => {
		const configTypes = property.Editor?.MediaTypes;
		if (!configTypes || configTypes.length === 0) {
			return ['VIDEO', 'IMAGE', 'CAROUSEL_ALBUM'];
		}
		const types: string[] = [];
		if (configTypes.includes('VIDEO')) {
			types.push('VIDEO');
		}
		if (configTypes.includes('IMAGE')) {
			types.push('IMAGE');
			types.push('CAROUSEL_ALBUM');
		}
		return types;
	};

	const getMaxMediaNumber = () => {
		const configNumber = property.Editor?.MaxMediaNumber || 1;
		return configNumber;
	};

	const impersonatingServices = new ImpersonatingServices(
		rootScope,
		routeParams
	);

	const [selectorIsOpened, setSelectorIsOpened] = useState<boolean>(false);
	const [accessTokenChecked, setAccessTokenChecked] = useState<boolean>(false);
	const [apiIsConfigured, setApiIsConfigured] = useState<boolean>(false);
	const [canAccessMedia, setCanAccessMedia] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [checkApiLoading, setCheckApiLoading] = useState<boolean>(false);
	const [selectedMedias, setSelectedMedias] = useState<string[]>(data?.split(',').filter(x => x) ?? []);
	const [filteredMediaTypes] = useState<string[]>(getFilteredMediaTypes());
	const [maxMediaNumber] = useState<number>(getMaxMediaNumber());

	const [previewMediaUrls, setPreviewMediaUrls] = useState<string[]>([]);

	const [mediaPage, setMediaPage] = useState<InstagramMediaContentData[]>([]);
	const [noData, setNoData] = useState<boolean | undefined>(undefined);
	const [paging, setPaging] = useState<{ previousPageCursor: string | null, nextPageCursor: string | null }>({ previousPageCursor: null, nextPageCursor: null });

	// const siblingProperties = propertyContainer && propertyContainer.siblingPropertiesAccessor;
	// const getPermalinkProperty = () => siblingProperties.getSiblingPropertyData(property.Editor.PermalinkPropertyName);
	// const setPermalinkProperty = (v) => siblingProperties.setSiblingPropertyData(property.Editor.PermalinkPropertyName, v);

	const refreshMedia = (requestedPage?: { previousPageCursor: string | null, nextPageCursor: string | null }) => {
		setIsLoading(true);
		insta.getMediaListPage(requestedPage?.previousPageCursor ?? null, requestedPage?.nextPageCursor ?? null,
			page => {
				setIsLoading(false);
				setPaging({ previousPageCursor: page.paging?.cursors?.before ?? null, nextPageCursor: page.paging?.cursors?.after ?? null });
				setMediaPage(page.data ?? []);
				if (noData === undefined) {
					setNoData(page.data === undefined || page.data.length === 0);
				}
				setCanAccessMedia(true);
			},
			() => {
				setIsLoading(false);
				setCanAccessMedia(false);
			});
	};

	const checkApiConfiguration = () => {
		setCheckApiLoading(true);
		insta.apiIsConfigured(({ isConfigured, isError }) => {
			setAccessTokenChecked(true);
			if (isError) {
				setApiIsConfigured(false);
			}
			if (isConfigured) {
				setApiIsConfigured(isConfigured);
			}
			setTimeout(() => setCheckApiLoading(false), 1000);
		});
	};

	const handleSelectMedia = (media) => {
		const isSelected = selectedMedias.some((m) => m === media.id);
		if (isSelected) {
			const tmpSelectedMedias = selectedMedias.filter((m) => m !== media.id);
			setSelectedMedias(tmpSelectedMedias);
		} else {
			if (maxMediaNumber === 1) {
				setSelectedMedias([media.id]);
			} else if (maxMediaNumber > selectedMedias.length) {
				setSelectedMedias(old => [...old, media.id]);
			}
		}
	};

	const resetSelectedMedias = () => {
		setSelectedMedias([]);
		onChange(InstagramMediaValue.fromValues(null));
	};

	useEffect(() => {
		checkApiConfiguration();
	}, []);

	useEffect(() => {
		if (!selectorIsOpened) return;
		if (!apiIsConfigured) return;

		refreshMedia();
	}, [selectorIsOpened, apiIsConfigured]);

	useEffect(() => {
		(async () => {
			const mediaFromData = data?.split(',').slice(0, 3) ?? [] as string[];
			const urls = [] as string[];
			for (let index = 0; index < mediaFromData.length; index++) {
				const mediaId = mediaFromData[index];
				const media = await insta.getMediaContentAsync(mediaId);
				if (media.thumbnail_url) {
					urls.push(media.thumbnail_url);
				} else if (media.media_url) {
					urls.push(media.media_url);
				}
			}
			setPreviewMediaUrls(urls);
		})();
	}, [data]);

	const goPrevious = () => {
		refreshMedia({ ...paging, nextPageCursor: null });
	};
	const goNext = () => {
		refreshMedia({ ...paging, previousPageCursor: null });
	};

	const validateSelection = () => {
		onChange(InstagramMediaValue.fromValues(selectedMedias));
		setSelectorIsOpened(false);
	};

	const apiNoConfiguredSection = () => <>
		<EmptyState
			textSize='l'
			width='l'
			verticalAlign={true}
			icon="fab fa-instagram"
			title="Instagram API is not configured"
			text="To access images and videos from your Instagram account, you need to configure the Instagram API in your settings."
			primaryAction={
				<>
					<Btn
						href={impersonatingServices.getImpersonatedUrl('/EditAccount?tab=instagram')}
						target="_blank"
						message="Go to settings"
						icon="fas fa-external-link-square-alt"
						iconPosition="after"
					/>
					<div className='grey_3 s_14 mt_20'>
						If the setup has already been done, please
						<Btn
							onClick={() => checkApiConfiguration()}
							message="click here to refresh"
							icon="fas fa-sync-alt"
							style="link"
							size="s"
							className="ml_5"
							isLoading={checkApiLoading}
						/>
					</div>
				</>
			}
		/>
	</>;

	const canNotAccessMediaSection = () => <>
		<EmptyState
			textSize='l'
			width='l'
			verticalAlign={true}
			icon="fab fa-instagram"
			title="Can not access to Instagram account"
			text="Please check the setup in your settings."
			primaryAction={
				<>
					<Btn
						href={impersonatingServices.getImpersonatedUrl('/EditAccount?tab=instagram')}
						target="_blank"
						message="Go to settings"
						icon="fas fa-external-link-square-alt"
						iconPosition="after"
					/>
					<div className='grey_3 s_14 mt_20'>
						If the setup has already been modify, please
						<Btn
							onClick={() => checkApiConfiguration()}
							message="click here to refresh"
							icon="fas fa-sync-alt"
							style="link"
							size="s"
							className="ml_5"
							isLoading={checkApiLoading}
						/>
					</div>
				</>
			}
		/>
	</>;

	const emptyStateSection = (hasMedia) => <>
		<EmptyState
			textSize='l'
			width='l'
			verticalAlign={true}
			icon="fab fa-instagram"
			title={hasMedia ? 'No more media' : 'No media on your Instagram account'}
		/>
	</>;

	const checkingApiConfigurationSection = () => <>
		<div>Checking Instagram API configuration..</div>
	</>;

	const displayPaging = () => (
		<div className={styles.pagin}>
			{paging.nextPageCursor &&
				<Btn
					onClick={() => goPrevious()}
					message='Previous'
					color="secondary"
					style="outline"
					icon="fas fa-chevron-left fa-sm"
					iconPosition="before"
					disabled={isLoading}
				/>
			}
			{paging.previousPageCursor &&
				<Btn
					onClick={() => goNext()}
					message='Next'
					color="secondary"
					style="outline"
					icon="fas fa-chevron-right fa-sm"
					iconPosition="after"
					disabled={isLoading}
				/>
			}
		</div>
	);

	const mediaListSection = () => {
		if (isLoading) return (
			<Spinner />
		);

		if (!isLoading && !canAccessMedia) return canNotAccessMediaSection();
		if (mediaPage.length === 0) return (emptyStateSection(!noData));

		return (
			<div className={styles.gallery}>
				{mediaPage.filter(media => media.id !== undefined).map((media) => (
					<Media
						key={media.id}
						media={media}
						filteredMediaTypes={filteredMediaTypes}
						selectedMedias={selectedMedias}
						handleSelectMedia={handleSelectMedia}
						maxMediaNumber={maxMediaNumber}
					/>
				))}
			</div>
		);
	};

	const browseBTNLabel = (types: string[]) => {
		const hasVideo = types.some((t) => t === 'VIDEO');
		if (hasVideo && types.length > 1) return 'Browse medias';
		if (hasVideo) return 'Browse videos';
		return 'Browse images';
	};

	const emptyLabel = (types: string[]) => {
		const hasVideo = types.some((t) => t === 'VIDEO');
		if (hasVideo && types.length > 1) return 'No media selected';
		if (hasVideo) return 'No video selected';
		return 'No image selected';
	};

	const titleLabel = (types: string[], max) => {
		const hasVideo = types.some((t) => t === 'VIDEO');
		if (hasVideo && types.length > 1) {
			if (max === 1) {
				return 'Select 1 media from your Instagram account';
			}
			return 'Select medias from your Instagram account';
		}
		if (hasVideo) {
			if (max === 1) {
				return 'Select 1 video from your Instagram account';
			}
			return 'Select videos from your Instagram account';
		}
		if (max === 1) {
			return 'Select 1 image from your Instagram account';
		}
		return 'Select images from your Instagram account';
	};

	const panelTitle = () => (
		<div className='flex'>
			<div className='flex_item_auto'>
				{titleLabel(filteredMediaTypes, maxMediaNumber)}
				<span className='ml_5'>({selectedMedias.length}/{maxMediaNumber})</span>
			</div>
			{selectedMedias.length > 0 &&
				<div className='flex_item_fix ml_10'>
					<Btn
						message="Unselect"
						icon="fas fa-ban"
						onClick={resetSelectedMedias}
						style="outline"
						color="secondary"
						size="xs"
						horizontalSize="xxs"
					/>
				</div>
			}
		</div>
	);
	
	const mediaFromData = data?.split(',').filter(x => x) ?? [];

	return (
		<>
			<div className='flex'>
				<div className='flex_item_full'>
					{mediaFromData && mediaFromData.length > 0 ?
						<div className={styles.thumb_list}>
							{previewMediaUrls.slice(0, 3).map((d, i) =>
								<div key={i} className={styles.thumb}>
									<img src={d} className={styles.thumb_image} />
								</div>
							)}
							{mediaFromData.length > 3 &&
								<div className={styles.thumb_ellips}>
									+{mediaFromData.length - 3}
								</div>
							}
						</div>
						:
						<div className={styles.thumb_list_empty}>
							{emptyLabel(filteredMediaTypes)}
						</div>
					}
				</div>
				<div className='flex_item_fix ml_10'>
					<Btn
						onClick={() => setSelectorIsOpened(true)}
						message={browseBTNLabel(filteredMediaTypes)}
						color="primary"
						style="outline"
						size="s"
					/>
				</div>
				<div className='flex_item_fix ml_10'>
					<Btn
						onClick={() => resetSelectedMedias()}
						disabled={selectedMedias.length === 0}
						icon="fas fa-ban"
						tooltip="Reset selected medias"
						color="secondary"
						style="outline"
						size="s"
						horizontalSize="xxs"
					/>
				</div>
			</div>
			<Panel
				isOpen={selectorIsOpened}
				width={1240}
			>
				<PanelHeader
					title={panelTitle()}
					onClose={() => setSelectorIsOpened(false)}
					primaryAction={
						<Btn
							message="Done"
							color="primary"
							disabled={selectedMedias.length === 0}
							onClick={validateSelection}
						/>
					}
				/>
				<PanelBody color={accessTokenChecked && apiIsConfigured && canAccessMedia ? 'grey' : 'white'} position='relative'>
					{!accessTokenChecked &&
						checkingApiConfigurationSection()
					}
					{accessTokenChecked && !apiIsConfigured &&
						apiNoConfiguredSection()
					}
					{accessTokenChecked && apiIsConfigured &&
						mediaListSection()
					}
				</PanelBody>
				{accessTokenChecked && apiIsConfigured && canAccessMedia &&
					<PanelFooter>
						{displayPaging()}
					</PanelFooter>
				}
			</Panel>
		</>
	);
}

function Media({
	media,
	selectedMedias,
	filteredMediaTypes,
	handleSelectMedia,
	maxMediaNumber
}: {
	media: InstagramMediaContentData,
	selectedMedias: string[],
	filteredMediaTypes: string[],
	handleSelectMedia: (media: InstagramMediaContentData) => void,
	maxMediaNumber: number
}) {

	const cssClass: string[] = [styles.media];
	const isSelected = selectedMedias.some((m) => m === media.id);
	const maxIsReached = maxMediaNumber > 1 && maxMediaNumber <= selectedMedias.length;
	const isEnabled = (isSelected || !maxIsReached) && filteredMediaTypes.some((t) => t === media.media_type);
	const [previewIsOpen, setPreviewIsOpen] = useState(false);

	cssClass.push(isSelected ? styles.media_selected : styles.media_unselected);
	cssClass.push(isEnabled ? styles.media_enabled : styles.media_disabled);

	const handleClick = () => {
		if (!isEnabled) return;
		handleSelectMedia(media);
	};

	return (
		<>
			<div className={cssClass.join(' ')} onClick={handleClick}>
				{media.media_type === 'VIDEO' &&
					<img src={media.thumbnail_url} className={styles.video} />
				}
				{media.media_type !== 'VIDEO' &&
					<>
						<img src={media.media_url} className={styles.image_background_1} />
						<img src={media.media_url} className={styles.image_background_2} />
						<img src={media.media_url} className={styles.image} />
					</>
				}

				<div className={styles.media_header}>
					<div className={styles.media_checkbox}>
						<Checkbox
							hasLabel={false}
							forcedChecked={selectedMedias.some((m) => m === media.id)}
							theme='light'
							type={maxMediaNumber === 1 ? 'radio' : 'checkbox'}
						/>
					</div>
					<div className={styles.media_type}>
						{media.media_type === 'CAROUSEL_ALBUM' &&
							<svg fill="currentColor" height="20" width="20" role="img" viewBox="0 0 48 48"><path d="M34.8 29.7V11c0-2.9-2.3-5.2-5.2-5.2H11c-2.9 0-5.2 2.3-5.2 5.2v18.7c0 2.9 2.3 5.2 5.2 5.2h18.7c2.8-.1 5.1-2.4 5.1-5.2zM39.2 15v16.1c0 4.5-3.7 8.2-8.2 8.2H14.9c-.6 0-.9.7-.5 1.1 1 1.1 2.4 1.8 4.1 1.8h13.4c5.7 0 10.3-4.6 10.3-10.3V18.5c0-1.6-.7-3.1-1.8-4.1-.5-.4-1.2 0-1.2.6z"></path></svg>
						}
						{media.media_type === 'VIDEO' &&
							<svg fill="currentColor" height="20" width="20" role="img" viewBox="0 0 24 24"><path d="m12.823 1 2.974 5.002h-5.58l-2.65-4.971c.206-.013.419-.022.642-.027L8.55 1Zm2.327 0h.298c3.06 0 4.468.754 5.64 1.887a6.007 6.007 0 0 1 1.596 2.82l.07.295h-4.629L15.15 1Zm-9.667.377L7.95 6.002H1.244a6.01 6.01 0 0 1 3.942-4.53Zm9.735 12.834-4.545-2.624a.909.909 0 0 0-1.356.668l-.008.12v5.248a.91.91 0 0 0 1.255.84l.109-.053 4.545-2.624a.909.909 0 0 0 .1-1.507l-.1-.068-4.545-2.624Zm-14.2-6.209h21.964l.015.36.003.189v6.899c0 3.061-.755 4.469-1.888 5.64-1.151 1.114-2.5 1.856-5.33 1.909l-.334.003H8.551c-3.06 0-4.467-.755-5.64-1.889-1.114-1.15-1.854-2.498-1.908-5.33L1 15.45V8.551l.003-.189Z" fillRule="evenodd"></path></svg>
						}
					</div>
				</div>

				<div style={{ position: 'absolute', zIndex: 1, bottom: 0, color: 'white' }}>
					<div><a onClick={() => handleSelectMedia(media)}>{media.id}</a></div>
				</div>

				{media.media_type === 'VIDEO' &&
					<div className={styles.media_footer}>
						<button className={styles.media_action} onClick={(e) => { setPreviewIsOpen(true); e.stopPropagation(); }}>
							<i className='fas fa-play'></i>
						</button>
					</div>
				}
			</div>
			{media.media_type === 'VIDEO' &&
				<Modal
					isOpen={previewIsOpen}
					onClose={() => setPreviewIsOpen(false)}
					width={400}
				>
					<ModalBody hasVerticalPadding={false} hasHorizontalPadding={false}>
						<video
							src={media.media_url}
							style={{ width: '100%', display: 'block' }}
							controls={true}
							autoPlay={true}
						/>
					</ModalBody>
				</Modal>
			}
		</>
	);
}